// shared/baseQuery.js
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import getAuthToken, { clearAuth0Cache, loginWithRedirect } from "../auth/authManager";
import { Mutex } from 'async-mutex';

// Prevent multiple refresh attempts
const mutex = new Mutex();

// Enhanced base query with async token and auto-retry on 401
export const baseQueryWithAuth = async (args, api, extraOptions) => {
  // Wait for any ongoing refresh to complete
  await mutex.waitForUnlock();

  const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    mode: "cors",
    prepareHeaders: async (headers) => {
      const token = await getAuthToken();
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  });

  let result = await baseQuery(args, api, extraOptions);

  // If we get a 401, try to refresh the token and retry
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      
      try {
        console.log('Got 401, attempting to refresh token...');
        
        // Clear cache to force token refresh
        clearAuth0Cache();
        
        // Get fresh token
        const newToken = await getAuthToken();
        
        if (newToken) {
          console.log('Token refreshed successfully, retrying request...');

          // Retry the original request with new token
          const retryQuery = fetchBaseQuery({
            baseUrl: process.env.REACT_APP_API_URL,
            mode: "cors",
            prepareHeaders: (headers) => {
              headers.set("authorization", `Bearer ${newToken}`);
              return headers;
            },
          });

          result = await retryQuery(args, api, extraOptions);
        } else {
          console.log('Token refresh failed - redirecting to login...');
          // Token refresh failed, redirect to login
          try {
            await loginWithRedirect();
          } catch (redirectError) {
            console.error('Failed to redirect to login:', redirectError);
          }
        }

      } catch (error) {
        console.error('Token refresh error:', error);
        // Token refresh failed, redirect to login
        try {
          await loginWithRedirect();
        } catch (redirectError) {
          console.error('Failed to redirect to login:', redirectError);
        }
      } finally {
        release();
      }
    } else {
      // Wait for ongoing refresh and retry
      await mutex.waitForUnlock();
      const token = await getAuthToken();
      if (token) {
        const retryQuery = fetchBaseQuery({
          baseUrl: process.env.REACT_APP_API_URL,
          mode: "cors",
          prepareHeaders: (headers) => {
            headers.set("authorization", `Bearer ${token}`);
            return headers;
          },
        });
        result = await retryQuery(args, api, extraOptions);
      }
    }
  }

  return result;
};